import type { User, UserGroup } from '@gmn-portal/backend';

export const SERVICE_KEYS = [
  'customers',
  'objects',
  'contracts',
  'operations',
  'operation-files',
  'users',
  'notifications',
  'object-categories',
] as const;

export const DEFAULT_GMN_GROUP_KEYS = ['gmn-admin', 'juh-dispatcher', 'dgzrs-billing'] as const;

export const DEFAULT_CUSTOMER_GROUP_KEYS = [
  'customer-admin',
  'customer-user',
  'heli-provider',
] as const;

export const HELI_PROVIDER_GROUP_KEY = 'heli-provider';

export const CUSTOMER_MANAGER_GROUP_KEYS = ['customer-admin', 'heli-provider'] as const;

export const CUSTOMER_ADMIN_ASSIGNABLE_GROUP_KEYS = DEFAULT_CUSTOMER_GROUP_KEYS.filter(
  (key) => key !== HELI_PROVIDER_GROUP_KEY
);

export const DEFAULT_GROUP_ORDER = [
  ...DEFAULT_GMN_GROUP_KEYS,
  ...DEFAULT_CUSTOMER_GROUP_KEYS,
] as const;

export type ServiceKey = (typeof SERVICE_KEYS)[number];

export type CrudAction = 'create' | 'read' | 'update' | 'delete';

export const SCOPE_OPTIONS = [
  'all',
  'customer-assignment',
  'user-assignment',
  'explicit-approval',
] as const;

export type ScopeOption = (typeof SCOPE_OPTIONS)[number];

export type UserGroupDomain = 'gmn' | 'customer';

export type ServiceDefinition = {
  key: ServiceKey;
  labelKey: string;
  includeInGroupsUi: boolean;
  supportsAcl?: boolean;
  allowedScopes?: ScopeOption[];
};

export const serviceRegistry: Record<ServiceKey, ServiceDefinition> = {
  customers: {
    key: 'customers',
    labelKey: 'permissions.services.customers',
    includeInGroupsUi: true,
    allowedScopes: ['all', 'customer-assignment'],
  },
  objects: {
    key: 'objects',
    labelKey: 'permissions.services.objects',
    includeInGroupsUi: true,
    supportsAcl: true,
    allowedScopes: ['all', 'customer-assignment', 'explicit-approval'],
  },
  contracts: {
    key: 'contracts',
    labelKey: 'permissions.services.contracts',
    includeInGroupsUi: true,
    supportsAcl: true,
    allowedScopes: ['all', 'customer-assignment', 'explicit-approval'],
  },
  operations: {
    key: 'operations',
    labelKey: 'permissions.services.operations',
    includeInGroupsUi: true,
    supportsAcl: true,
  },
  'operation-files': {
    key: 'operation-files',
    labelKey: 'permissions.services.operationFiles',
    includeInGroupsUi: false,
  },
  users: {
    key: 'users',
    labelKey: 'permissions.services.users',
    includeInGroupsUi: true,
    allowedScopes: ['all', 'customer-assignment'],
  },
  notifications: {
    key: 'notifications',
    labelKey: 'permissions.services.notifications',
    includeInGroupsUi: false,
  },
  'object-categories': {
    key: 'object-categories',
    labelKey: 'permissions.services.objectCategories',
    includeInGroupsUi: false,
  },
};

export const GROUP_SERVICE_ORDER: ServiceKey[] = [
  'customers',
  'objects',
  'contracts',
  'operations',
  'users',
];

export const ACL_SERVICE_KEYS = SERVICE_KEYS.filter(
  (key) => serviceRegistry[key].supportsAcl === true
) as ServiceKey[];

export type AclServiceKey = (typeof ACL_SERVICE_KEYS)[number];

const groupOrderMap = new Map<string, number>(
  DEFAULT_GROUP_ORDER.map((key, index) => [key, index])
);

export const sortUserGroupsByKey = <T extends { key: string }>(groups: T[]): T[] => {
  return [...groups].sort((a, b) => {
    const aOrder = groupOrderMap.get(a.key) ?? Number.POSITIVE_INFINITY;
    const bOrder = groupOrderMap.get(b.key) ?? Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.key.localeCompare(b.key);
  });
};

export const resolveUserGroup = (user: User | null | undefined): UserGroup | null => {
  const group = user?.userGroup;
  if (!group || typeof group === 'string') return null;
  return group as UserGroup;
};

export const resolveUserGroupKey = (user: User | null | undefined): string | null => {
  const group = resolveUserGroup(user);
  return group?.key ?? null;
};

export const resolveUserGroupDomain = (user: User | null | undefined): UserGroupDomain | null => {
  const group = resolveUserGroup(user);
  if (!group) return null;
  return group.domain as UserGroupDomain;
};

export const isGmnDomain = (user: User | null | undefined): boolean =>
  resolveUserGroupDomain(user) === 'gmn';

export const isCustomerDomain = (user: User | null | undefined): boolean =>
  resolveUserGroupDomain(user) === 'customer';

export const isGmnAdminGroup = (user: User | null | undefined): boolean =>
  resolveUserGroupKey(user) === 'gmn-admin';

export const isCustomerAdminGroup = (user: User | null | undefined): boolean =>
  resolveUserGroupKey(user) === 'customer-admin';

export const isHeliProviderGroup = (user: User | null | undefined): boolean =>
  resolveUserGroupKey(user) === HELI_PROVIDER_GROUP_KEY;

export const isCustomerManagerGroup = (user: User | null | undefined): boolean =>
  CUSTOMER_MANAGER_GROUP_KEYS.includes(
    resolveUserGroupKey(user) as (typeof CUSTOMER_MANAGER_GROUP_KEYS)[number]
  );

export const hasServicePermission = (
  group: UserGroup | null,
  service: ServiceKey,
  action: CrudAction
): boolean => {
  if (!group) return false;
  const entry = group.permissions?.find((perm) => perm.service === service);
  if (!entry) return false;
  return Boolean(entry[action]);
};

export const getServiceScopes = (group: UserGroup | null, service: ServiceKey): ScopeOption[] => {
  if (!group) return [];
  return (group.scopes || [])
    .filter((scopeEntry) => scopeEntry.service === service)
    .map((scopeEntry) => scopeEntry.scope as ScopeOption);
};

export const canReadService = (group: UserGroup | null, service: ServiceKey): boolean => {
  if (!group) return false;
  const scopes = getServiceScopes(group, service);
  return hasServicePermission(group, service, 'read') && scopes.length > 0;
};

export const canUpdateService = (group: UserGroup | null, service: ServiceKey): boolean => {
  if (!group) return false;
  return hasServicePermission(group, service, 'update');
};

export const canCreateService = (group: UserGroup | null, service: ServiceKey): boolean => {
  if (!group) return false;
  return hasServicePermission(group, service, 'create');
};

export const canDeleteService = (group: UserGroup | null, service: ServiceKey): boolean => {
  if (!group) return false;
  return hasServicePermission(group, service, 'delete');
};
